/**
 * 分类选择器 —— **可搜索下拉 + 两段式下钻**（2026-10-04 重做 · 2026-10-28 批 `115` 改为可展开）。
 *
 * 依据：**B 域 PRD §5.4.3**（渐进披露）· **§5.8**（拼音模糊搜索，`AC-B4.4` / `AC-B4.7` / `AC-B7.*`）·
 * `DR-066` §5（两段式下钻的定案）。
 *
 * ## 🔴 为什么从「chips 三级渐进披露」改成下拉
 *
 * 原实现是一排 chips：常用 → 点「全部 N 类」→ 选中 L1 才露出它的 L2。
 * 负责人 2026-10-04 指出这套交互的三个问题：
 *
 * 1. **看不到全貌** —— 二级分类必须先点中它的一级才出现，用户没法"扫描一遍再决定"。
 * 2. **必须用鼠标点** —— 分类有 **103 个 L2**，纯点选在长列表上不可用（§5.8 正文明说
 *    「长列表纯点选不可用，『键盘直接键入』是记账效率的硬前提」）。
 * 3. **层级关系不可见** —— 一排平铺的 chip 看不出谁是谁的子类。
 *
 * ⇒ 改成**下拉**：一次展开即可纵览；**一级顶格、二级缩进**，父子关系一眼可见；
 * 且**直接键入**（含拼音）即可过滤 —— 这正是 §5.8 要的能力，两个需求在此合并成一个组件。
 *
 * ## 🆕 批 `115`：首屏只列一级，行尾 `▸` 就地展开（`DR-066` §5 · `AC-B5.8`）
 *
 * 批 `110` 把空输入收窄成"常用 12 个一级"，副作用是**二级必须键入才出现**
 * （负责人 2026-10-28："二级要打字才出来，不好用"）。现在：
 *
 * | 状态 | 列什么 |
 * |---|---|
 * | 没打字 | **全部一级**（不是常用集）；**已展开**的一级紧跟其后插入它的全部二级 |
 * | 一键入 | **全量搜**（跨级；未展开的二级也搜得到） |
 *
 * - 点**行文字** = 选中该分类（选一级 = 记在大类，合法记账）；
 * - 点**行尾 `▸`** = 只展开/收起，**不选中**（`onMouseDown` preventDefault + `onClick` stopPropagation，
 *   缺一个按钮就点不到或被当成选中，批 `115` 实测）；
 * - 展开态是**每个实例各自**的 `useState<Set<string>>`（拆分行每行一份，可接受）——
 *   提到页面级会让同一屏里的几行互相影响，正是批 `110` 撤掉档位按钮的原因之一。
 *
 * ## 🔴 三条不变量（与旧实现一致，**不许松动**）
 *
 * 1. **顺序不在本组件重排** —— 直接用服务端给的行序（`contract/category.ts` 明文：
 *    顺序即展示顺序）。本组件只做**分组**与**缩进**，不排序。
 * 2. **值语义三态不变** —— `null` = 未分类 / L1 id = 记在大类 / L2 id = 记在子类。
 *    下拉里 L1 与 L2 **都是可选项**（选 L1 = 整类未细分，是合法记账）。
 * 3. **空态必须给下一步动作**（`AC-B4.7`）—— 该方向一个分类都没有时，明确告诉用户
 *    「可以先不选分类直接记账」。
 *
 * ## ⚠️ 本组件**不含**什么（诚实登记）
 *
 * - **图标**：`iconToken` 已随接口下发，但字形池的跨端产物尚不存在（O 域收敛批）⇒ 不画。
 * - **常用集**：`common` 标记仍在契约里（`AC-B4.1`），但**不再用来限制列表**（`DR-066` §5）。
 *   `toCommonOptions()` 保留（`AC-B4.8` 自定义常用集落地时直接用），本批不接。
 */

import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CategoryKind, CategoryView } from '@jiaying/shared';

import {
  createCategory,
  listCategoriesWithRemoved,
  removeCategory,
  restoreCategory,
  updateCategory,
} from '../api/endpoints.js';
import {
  isExpandable,
  toggleExpanded,
  visibleCategoryOptions,
} from '../features/category-options.js';
import { SearchSelect } from './SearchSelect.js';
import type { SearchSelectOption } from './SearchSelect.js';
import { CategoryManageDialog } from './CategoryManageDialog.js';

export interface CategoryPickerProps {
  /** 当前方向（支出 / 收入）—— 只影响**过滤**，不影响数据获取（全量已在手，切换不重取） */
  readonly kind: CategoryKind;
  /** 服务端下发的**全量**分类（含两个方向、L1 与 L2） */
  readonly items: readonly CategoryView[];
  /** 当前选中项：`null` = 未分类 · L1 id = 记在大类 · L2 id = 记在子类 */
  readonly value: string | null;
  readonly onChange: (categoryId: string | null) => void;
  /** 下拉标签（单腿 = 「分类」；多腿里可传「分类（可留空）」这类更明确的说法） */
  readonly label?: string;
  /**
   * 🆕 是否渲染下方的说明（「缩进的是二级细分…」）。
   *
   * 🔴 拆分子笔行（`DR-043`：一行 4 列）**必须传 `false`** —— 每行都带这行说明会把
   * 「一行」撑成两行，正是负责人要消掉的篇幅。说明改由**表头/区块说明**统一给一次。
   */
  readonly showHint?: boolean;
  /**
   * 🆕 批 `112-A`：是否显示「管理分类」入口（`DR-062` D1）。
   *
   * 🔴 **默认 `false`，只有"记一笔"与"流水筛选"两处传 `true`** ——
   * `DR-020` B6 把分类 CRUD 定位成「记账高频动作的伴生操作」，
   * 所以预算表单、计划对话框这些地方**不该**出现分类管理入口（那是把入口又散开了）。
   */
  readonly manageable?: boolean;
}

/** 下拉项：`CategoryView` + 缩进层级（**缩进只影响呈现，不参与匹配**） */
export interface CategoryOption extends SearchSelectOption {
  readonly indent: 0 | 1;
  readonly level: 1 | 2;
  /** 父级 id（L1 为 `null`）—— 展开态靠它判归属（`category-options.ts`） */
  readonly parentId: string | null;
  /** 该 L1 有几个二级（L2 恒 0）—— 行尾 `▸` 的判据 */
  readonly childCount: number;
}

/**
 * 把**服务端行序**的分类摊平成一维下拉项，并给 L2 标上缩进。
 *
 * 🔴 **不做排序**：按服务端给的顺序走。但需要保证「L2 紧跟它父级」——
 * 若服务端给的是「先全部 L1、再全部 L2」的扁平序，直接渲染会让缩进失去意义。
 * 因此这里按**树的深度优先**重排：每个 L1 后紧跟它的所有 L2。
 *
 * ⚠️ 这是**呈现层的重组，不是业务排序** —— 它用的仍是服务端给的两个事实
 * （`id` / `parentId`）与行内相对顺序，没有引入任何端侧硬编的顺序偏好。
 *
 * 🔴 **为什么导出**（而不是留在组件内部）：下拉**展开后的选项列表**在
 * `renderToStaticMarkup` 基座里**看不到**（MUI Autocomplete 仅在打开时挂载弹层，
 * 而打开需要事件 —— 见 `test/render.tsx` 的升级触发线 ①）。
 * ⇒ 树序 / 缩进 / 方向过滤这些**纯逻辑**必须**直接对函数测**，
 * 而不是绕道渲染出的 DOM 去猜（那样只能拿到空列表，是**假信号**）。
 */
export function toOptions(items: readonly CategoryView[], kind: CategoryKind): CategoryOption[] {
  const l1 = items.filter((c) => c.kind === kind && c.level === 1);
  const out: CategoryOption[] = [];
  for (const parent of l1) {
    const children = items.filter((c) => c.parentId === parent.id);
    out.push({
      id: parent.id,
      label: parent.name,
      // 分类名参与匹配就是它自己的中文名（§5.8.2 会同时建全拼与首字母索引）
      matchText: parent.name,
      indent: 0,
      level: 1,
      parentId: null,
      // 🔴 决定行尾有没有 `▸`（无子类的 L1 不给无意义的点击）
      childCount: children.length,
    });
    for (const child of children) {
      out.push({
        id: child.id,
        label: child.name,
        matchText: child.name,
        indent: 1,
        level: 2,
        parentId: parent.id,
        childCount: 0,
      });
    }
  }
  return out;
}

/**
 * 🆕 批 `107`（K11 · `AC-B4.1` / `AC-B4.4` 第 1 级）—— **常用集**的下拉项（只 L1）。
 *
 * 🔴 **批 `115` 起本函数不再被 `CategoryPicker` 调用**（`DR-066` §5：常用集不再限制列表，
 * 首屏改为列全部一级）。保留它是因为 `AC-B4.8`（用户自定义常用集）落地时要直接用，
 * 且"哪些是常用"的口径只该有这一处 —— 但它**不再影响本组件的候选**。
 *
 * 🔴 **端侧对"哪些是常用"零知识**：本函数只读服务端下发的 `common === true`，
 * **不**引用 `COMMON_L1_IDS`、**不**硬编那 10 个 id（B 域 PRD §5.3.5「跨端单源」：
 * 将来 `AC-B4.8` 用户自定义常用集落地时，只在服务端换数据源，本函数**一行都不用改**）。
 */
export function toCommonOptions(items: readonly CategoryView[], kind: CategoryKind): CategoryOption[] {
  return items
    .filter((c) => c.kind === kind && c.level === 1 && c.common)
    .map((c) => ({
      id: c.id,
      label: c.name,
      matchText: c.name,
      indent: 0 as const,
      level: 1 as const,
      parentId: null,
      // 与 `toOptions` 同口径：有二级才标记可展开
      childCount: items.filter((x) => x.parentId === c.id).length,
    }));
}

export function CategoryPicker({
  kind,
  items,
  value,
  onChange,
  label = '分类',
  showHint = true,
  manageable = false,
}: CategoryPickerProps) {
  const all = toOptions(items, kind);
  /**
   * 🔴 **展开态是每个实例各自的**（拆分行每行一份）。
   * 提到页面级会让同一屏里几行互相影响 —— 那正是批 `110` 撤掉「档位按钮」的原因之一。
   * 不持久化：刷新后收起是对的行为（下次记账仍从一级开始扫）。
   */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set<string>());

  /**
   * 🆕 批 `112-A`：管理入口的接线（`DR-062` D1）。
   *
   * 🔴 hooks **无条件调用**，即使 `manageable === false` —— 因为下面的
   * `if (all.length === 0)` 是**早退**，hooks 必须排在它前面（否则两次渲染的 hooks 数量不同 ⇒ React 报错）。
   * 按钮与对话框才受 `manageable` 控制。
   *
   * 🔴 写成功后只 `invalidateQueries(['categories'])`，**不自己拼列表** ——
   * 分类的顺序/常用集/可见性口径都在服务端（`category.service`），端侧再算一次就是第二份口径。
   */
  const [manageOpen, setManageOpen] = useState(false);
  const queryClient = useQueryClient();
  const createMut = useMutation({
    mutationFn: createCategory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });
  const updateMut = useMutation({
    mutationFn: (v: { id: string; name: string }) => updateCategory(v.id, { name: v.name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });
  const removeMut = useMutation({
    mutationFn: removeCategory,
    // 🔴 `invalidateQueries` 是**前缀匹配**：这一个 key 同时刷掉 `['categories']` 与
    //    `['categories','withRemoved']`（两条都要刷 —— 否则「含已移除」区里还留着已恢复的那一行）
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });
  /**
   * 🆕 批 `122`：**已移除清单**（`DR-070` D1 —— 管理对话框的「含已移除」区靠它）。
   *
   * 🔴 独立 key + `enabled: manageOpen`：只在对话框打开时取。
   * 分类列表已有 146 行，让每个挂载的 picker 都白取一份已移除清单没有收益。
   * 🔴 用 112-B 那个**独立函数**（`listCategoriesWithRemoved`），不给 `listCategories` 加参数 ——
   * `queryFn: listCategories` 会把 query context 当第一实参传，可选参数会把所有选择器静默变成"含已移除"。
   */
  const removedQ = useQuery({
    queryKey: ['categories', 'withRemoved'],
    queryFn: listCategoriesWithRemoved,
    enabled: manageOpen,
  });
  const restoreMut = useMutation({
    mutationFn: restoreCategory,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['categories'] }),
  });

  // ① 该方向一个分类都没有 ⇒ 空态必须给下一步动作（`AC-B4.7`）
  if (all.length === 0) {
    return (
      <Alert severity="info" data-testid="cat-empty-kind">
        这个方向还没有可用分类。可以先不选分类直接记账 —— 在流水里随时能补（「先记后分类」）。
      </Alert>
    );
  }

  return (
    <>
      <SearchSelect<CategoryOption>
        label={label}
        // 🔴 `options` 恒为**全量**（搜索永远搜得到）；"列哪些"由 `optionFilter` 决定
        options={all}
        optionFilter={(opts, q) => visibleCategoryOptions(opts, expanded, q)}
        value={value ?? ''}
        onChange={(id) => onChange(id === '' ? null : id)}
        placeholder="选分类 · 可键入拼音或汉字搜（如 cs / 餐饮）"
        noOptionsText="无匹配项 —— 可先不选分类直接记账，之后再补"
        renderTrailing={(o) =>
          isExpandable(o) ? (
            <IconButton
              size="small"
              // 🔴 只写一个都不行：没有 preventDefault，Autocomplete 先失焦关弹层 ⇒ 按钮点不到；
              //    没有 stopPropagation，会顺带把该分类选中。
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((prev) => toggleExpanded(prev, o.id));
              }}
              aria-expanded={expanded.has(o.id)}
              aria-label={expanded.has(o.id) ? `收起 ${o.label} 的二级` : `展开 ${o.label} 的二级`}
              data-testid={`cat-expand-${o.id}`}
              sx={{ p: 0.25 }}
            >
              {/* 🔴 不用图标组件：字形池是 O 域的事，这里两个纯文本箭头最稳 */}
              <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>
                {expanded.has(o.id) ? '▾' : '▸'}
              </Box>
            </IconButton>
          ) : null
        }
      />
      {showHint && (
        <Typography variant="caption" color="text.secondary" data-testid="cat-hint">
          缩进的是二级细分 —— 点行尾 ▸ 展开；选一级 = 记在大类（整类未细分）。
        </Typography>
      )}
      {/*
        🆕 批 `112-A`：**「管理分类」入口**（`DR-062` D1 · `DR-020` B6「CRUD 回归记账上下文」）。
        🔴 只在 `manageable` 的两处出现（记一笔 / 流水筛选），不给预算、计划那些地方放 ——
        入口散开就等于又回到「到处都能改分类」的老问题。
      */}
      {manageable && (
        <Button
          size="small"
          variant="text"
          data-testid="cat-manage"
          onClick={() => {
            setManageOpen(true);
          }}
        >
          管理分类
        </Button>
      )}
      {manageable && (
        <CategoryManageDialog
          open={manageOpen}
          onClose={() => {
            setManageOpen(false);
          }}
          items={items}
          /*
            🆕 批 `122`：**只把已移除的那些**交给对话框（`DR-070` D1）。
            未取到时传空数组 ⇒ 开关显示「含已移除（0）」并置灰，不会闪一个假的计数。
          */
          removedItems={(removedQ.data?.items ?? []).filter((c) => c.removed === true)}
          kind={kind}
          onCreate={async (input) => {
            await createMut.mutateAsync({
              parentId: input.parentId,
              ...(input.kind === undefined ? {} : { kind: input.kind }),
              name: input.name,
              iconToken: input.iconToken,
            });
          }}
          onUpdate={async (id, input) => {
            await updateMut.mutateAsync({ id, name: input.name });
          }}
          onRemove={async (id) => {
            await removeMut.mutateAsync(id);
          }}
          onRestore={async (id) => {
            await restoreMut.mutateAsync(id);
          }}
        />
      )}
    </>
  );
}
